// src/components/LayerPanel/FeatureCatalogList.jsx
//
// The FEATURES sub-tab: the org's derived catalog, grouped into accordion
// sections by entitlement category. Reads useLayersStore.featureCatalog from
// GET /api/v1/feature-catalog and renders one toggle row per type — no
// comingSoon stubs. geom_class drives a small glyph; `origin` is a quiet tint
// only. Clicking a row toggles the layer via the generic renderer (or PadLayer,
// for well_pad).
//
// ── THE VOCABULARY COMES FROM THE DATABASE, NOT FROM THIS FILE ─────────────
//
// Each row carries `category`, `category_label`, `sort_order` and `grantable`,
// joined from aiprime.feature_categories by the endpoint. There is deliberately
// NO CATEGORY_LABELS or CATEGORY_ORDER constant here: REM-249 records five
// instances of one value living in two homes, four of them found in a single
// week, and every one failed silently in the direction that hides something.
// Adding a category is a row in feature_categories and nothing else.
//
// ── ONLY CATEGORIES THAT HOLD SOMETHING ARE DRAWN ─────────────────────────
//
// A header with nothing under it is an inventory disclosure of the same kind
// f950f0bd fixed on the catalog endpoint: it tells an org what categories exist
// without making them purchasable. Denial is invisible by design — an org sees
// what it is entitled to and sales explains the rest.
//
// `grantable` is sent and is NOT used to decide what to show. regulatory and
// land are both ungrantable for different reasons — regulatory is baseline
// access nobody buys and lives in the RRC sub-tab, land is customer-authored —
// and hiding on that flag would encode a coincidence. Emptiness is a fact about
// this org; grantability is a fact about the catalog.
//
// Owns its own search state. The fetch stays in FeaturesTab, so the catalog is
// warm whether or not this sub-tab has been opened.

import { useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { Search, X, ChevronRight } from "lucide-react";

import { useLayersStore } from "../../stores/useLayersStore";
import ToggleSwitch from "./ToggleSwitch";
import FeatureColorSwatch from "./FeatureColorSwatch";

function cx(...c) { return c.filter(Boolean).join(" "); }

// Quiet origin tint applied to the geom glyph — signals provenance without bins.
const ORIGIN_TINT = {
  inferred: "text-accent-teal/70",
  customer: "text-accent-blue/70",
  authoritative: "text-amber-400/70",
};
const ORIGIN_LABEL = {
  inferred: "AI-inferred",
  customer: "Customer-provided",
  authoritative: "Authoritative",
};
const GEOM_GLYPH = {
  point: "•", multipoint: "•",
  line: "―", multiline: "―",
  polygon: "▭", multipolygon: "▭",
};

// Group into sections, preserving the order the endpoint sent. The rows arrive
// sorted by sort_order then label, so a Map keyed on category yields sections in
// sort_order and rows alphabetical within them, with no second sort here.
//
// A row missing `category` — an older endpoint, a cached response mid-deploy —
// is not dropped. It lands in a single trailing section so a type is never
// silently invisible because the API shape moved. That case should not occur;
// the FK and the CHECK make it unreachable from a current server.
function groupByCategory(items) {
  const sections = new Map();
  for (const f of items) {
    const key = f.category ?? "__uncategorized";
    if (!sections.has(key)) {
      sections.set(key, {
        key,
        label: f.category_label ?? "Other",
        rows: [],
      });
    }
    sections.get(key).rows.push(f);
  }
  return [...sections.values()];
}

export default function FeatureCatalogList() {
  const [search, setSearch] = useState("");
  // EXPANDED, NOT COLLAPSED, and that inversion is the point: the category
  // keys arrive asynchronously from the catalog, so a collapsed-map cannot be
  // seeded with "all of them except one" at mount -- it does not know what
  // "all of them" is yet. Tracking the open ones instead makes unknown mean
  // closed, which is the default being asked for.
  //
  // wellsite is open because it holds the wells and sites a user is usually
  // looking for; the other six start closed so the panel opens as a list of
  // headings rather than a wall.
  const [expanded, setExpanded] = useState({ wellsite: true });

  const catalog = useLayersStore((s) => s.featureCatalog);
  const loaded = useLayersStore((s) => s.featureCatalogLoaded);
  const error = useLayersStore((s) => s.featureCatalogError);
  const toggleFeatureType = useLayersStore((s) => s.toggleFeatureType);
  // Subscribe to the two sources of "on" state so rows re-render on toggle.
  const activeFeatureTypes = useLayersStore((s) => s.activeFeatureTypes);
  const padsOn = useLayersStore(
    useShallow((s) => !!(s.assets?.pads?.enabled && s.assets?.pads?.geometry?.polygon))
  );

  const isOn = (key) => (key === "well_pad" ? padsOn : !!activeFeatureTypes[key]);

  const q = search.trim().toLowerCase();
  const searching = q.length > 0;

  const sections = useMemo(() => {
    const matched = (catalog ?? []).filter(
      (f) => !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
    );
    return groupByCategory(matched);
  }, [catalog, q]);

  // SEARCH FILTERS WITHIN SECTIONS AND FORCES THEM ALL OPEN, rather than
  // collapsing to a flat list.
  //
  // The failure this avoids is three matches hidden behind three collapsed
  // headers, which is worse than no grouping at all. Forcing open solves that
  // without a second layout: sections holding no match are already absent, since
  // groupByCategory only creates a section for a row that survived the filter,
  // so a search shows exactly the sections that contain something.
  //
  // Flattening while searching was the alternative and is rejected: it means two
  // render modes to keep consistent, and it throws away the category, which is
  // information the user is often searching FOR — "which of these is production
  // equipment" is a real question and a flat list cannot answer it.
  const isCollapsed = (key) => !searching && !expanded[key];

  const toggleSection = (key) =>
    setExpanded((e) => ({ ...e, [key]: !e[key] }));

  let body;
  if (!loaded) {
    body = <div className="px-1 py-1 text-[11px] text-fg-secondary">Loading features…</div>;
  } else if (error) {
    // DISTINCT FROM THE EMPTY STATE, DELIBERATELY. The message below tells a
    // user what they are entitled to; showing it for a network failure would
    // tell a paying customer they bought nothing.
    body = (
      <div className="px-1 py-1 text-[11px] text-amber-400/90">
        Could not load features. Retry, or contact support if this persists.
      </div>
    );
  } else if ((catalog ?? []).length === 0) {
    body = (
      <div className="px-1 py-1 text-[11px] text-fg-secondary italic">
        No feature layers are enabled for your organization. Regulatory data is
        available in the RRC tab.
      </div>
    );
  } else if (sections.length === 0) {
    body = <div className="px-1 py-1 text-[11px] text-fg-secondary italic">No matches.</div>;
  } else {
    body = (
      <div className="space-y-1.5">
        {sections.map((section) => {
          const open = !isCollapsed(section.key);
          return (
            <div
              key={section.key}
              className="rounded-md border border-slate-800 bg-bg-soft overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleSection(section.key)}
                aria-expanded={open}
                className="flex items-center gap-1.5 w-full px-2 py-1.5 text-left hover:bg-bg-softer transition"
              >
                <ChevronRight
                  size={11}
                  className={cx(
                    "text-fg-secondary flex-shrink-0 transition-transform",
                    open && "rotate-90"
                  )}
                />
                <span className="flex-1 min-w-0 text-[11px] text-gray-300 truncate">
                  {section.label}
                </span>
                <span className="flex-shrink-0 text-[10px] font-mono text-fg-secondary">
                  {section.rows.length}
                </span>
              </button>

              {open && (
                <div className="px-2 pb-2 space-y-1">
                  {section.rows.map((f) => {
                    const on = isOn(f.key);
                    const glyph = GEOM_GLYPH[f.geom_class] ?? "•";
                    return (
                      <div
                        key={f.key}
                        className={cx(
                          "group rounded border transition",
                          on
                            ? "bg-bg-softer border-slate-700"
                            : "bg-bg border-slate-800 hover:border-slate-700"
                        )}
                      >
                        <div className="flex items-center gap-1.5 px-2 py-[5px]">
                          {/* color swatch — effective color, doubles as legend + picker */}
                          <FeatureColorSwatch assetType={f.key} />
                          <span
                            className={cx(
                              "text-[11px] font-bold w-3 text-center flex-shrink-0",
                              on ? "text-gray-200" : ORIGIN_TINT[f.origin] ?? "text-fg-secondary"
                            )}
                            title={`${f.geom_class} · ${ORIGIN_LABEL[f.origin] ?? f.origin}`}
                          >
                            {glyph}
                          </span>
                          <button
                            onClick={() => toggleFeatureType(f.key)}
                            className="flex-1 text-left min-w-0"
                          >
                            <span
                              className={cx(
                                "text-[11px] truncate block",
                                on ? "text-gray-200" : "text-gray-400 hover:text-gray-200"
                              )}
                            >
                              {f.label}
                            </span>
                          </button>
                          <button
                            onClick={() => toggleFeatureType(f.key)}
                            className="flex-shrink-0"
                          >
                            <ToggleSwitch on={on} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="px-2 py-2 space-y-2">
      <div className="relative">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-secondary pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search features…"
          aria-label="Search features"
          className={cx(
            "w-full pl-6 pr-6 py-1.5 rounded-md border bg-bg text-[11px] text-gray-200",
            "placeholder-gray-600 focus:outline-none transition",
            search
              ? "border-accent-info/60 focus:border-accent-info"
              : "border-slate-700 focus:border-accent-info/60"
          )}
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-secondary hover:text-gray-200"
          >
            <X size={11} />
          </button>
        )}
      </div>

      {body}
    </div>
  );
}
